<script lang="ts">
  import Button from '@/lib/components/ui/button/button.svelte';
  import { dislikeWorkout, downloadActivityFitFile, downloadWorkoutFile, hideActivity, likeWorkout, scheduleTraining, starActivity, type WorkoutPlayerType } from '@/Util/api';
  import { Activity, type Exercise } from '@/Common/ActivityFunctions';
  import { workoutPlayerTypeToName } from '@/Common/constants';
  import { connectedWorkoutPlayers } from '@/Singletons/WorkoutPlayersWritable';
  import { getContext, onMount } from 'svelte';
  import type { MyFitnessStores } from '../stores';
  import * as Popover from '$lib/components/ui/popover/index.js';
  import { Label } from '@/lib/components/ui/label';
  import { Input } from '@/lib/components/ui/input';
  import { toDateInputString, toTimeInputString } from '@/Util/utils';
  import { addMinutes } from 'date-fns';
  import HelpTooltip from '@/Common/HelpTooltip.svelte';
  import { copyCalendarEvent, moveCalendarEvent, swapCalendarEvents } from '../Calendar/CalendarEventUtils';
  import type { CalendarLoader } from '../Calendar/CalendarLoaderNew';
  import { CreateCustomEventDispatchHandler } from '@/EventDispatchers';
  import ConfirmationPopover from './ConfirmationPopover.svelte';
  import { Separator } from '@/lib/components/ui/separator';
  import SendToStravaButton from './SendToStravaButton.svelte';
  import ShareActivityButton from './ShareActivityButton.svelte';
  import type { TodaysActivities } from '@/Common/constants';
  import { today } from '@/time';
  import { notifyApiError } from '@/Util/notify';
  import { ErrorCode } from '@/Util/errorCodes';
  import { askSlopeIntervals } from '@/Common/mixedMode';

  export let exercise: Exercise;
  export let loading: boolean = false;
  export let hideScheduleButtons: boolean = false;
  export let open: boolean = false;
  export let calendarLoader: CalendarLoader | null = null;
  export let rootDiv: HTMLElement | null = null;
  export let todaysActivities: TodaysActivities | null = null;

  // @ts-ignore
  const jq = window.jQuery;

  //@ts-ignore
  const Xert = window.Xert;
  const myFitness = getContext<MyFitnessStores>('myFitness');
  const trainingPlan = myFitness?.trainingPlan;

  // Derived state
  $: isFlagged = Activity.isFlagged(exercise);
  $: isPinned = Activity.isPinned(exercise);
  $: isPlanned = Activity.isPlanned(exercise);
  $: isStarred = Activity.isStarred(exercise);
  $: isForecast = Activity.isForecast(exercise);
  $: isForecastEvent = Activity.isForecastEvent(exercise);
  $: isPlaceholder = Activity.isPlaceholder(exercise);
  $: isRecorded = Activity.isRecorded(exercise);
  $: noActivity = Activity.noActivity(exercise);
  $: isGoalEvent = Activity.isGoalEvent(exercise);
  $: isHidden = Activity.isHidden(exercise);
  $: isActivity = Activity.isActivity(exercise);
  $: canEdit = Activity.canEdit(exercise);
  $: canMove = Activity.canMove(exercise);
  $: canCopy = Activity.canCopy(exercise);
  $: isDuplicate = Activity.isDuplicate(exercise);
  $: workoutPath = Activity.getWorkoutPath(exercise);
  $: garminLink = Activity.getGarminLink(exercise);
  // getExerciseType rather than exercise.exerciseType: it reports an Activity that carries a
  // workout_id as a "Workout", which is what someone reading the button would call it.
  $: trainingNoun = Activity.getExerciseType(exercise) ?? 'Training';

  // Schedule inputs. Default to the top of the *next* hour: scheduleTraining rejects times in the
  // past, so rounding down to the current hour made the default value unschedulable.
  let schedulePopoverOpen = false;
  let inputDate: string;
  let inputTime: string;
  $: if (open && !inputDate) {
    const d = new Date();
    d.setHours(d.getHours() + 1, 0, 0, 0);
    inputDate = toDateInputString(d);
    inputTime = toTimeInputString(d);
  }

  // Swap/Copy/Move inputs (default to activity's current date)
  let moveActivityPopoverOpen = false;
  let copyActivityPopoverOpen = false;
  let swapActivityPopoverOpen = false;
  let activityInputDate: string;
  let activityInputTime: string;
  $: if (!activityInputDate && exercise) {
    activityInputDate = toDateInputString(new Date(exercise.start_date));
    activityInputTime = toTimeInputString(new Date(exercise.start_date));
  }

  let scheduleIcon: string = 'fa-calendar-plus';
  $: exercise, scheduleIcon = 'fa-calendar-plus';

  let forecastIcon: string = 'fa-calendar-plus';
  $: exercise, forecastIcon = 'fa-calendar-plus';

  async function scheduleToForecast() {
    forecastIcon = 'fa-spinner-third animate-spin';
    const result = await scheduleTraining(exercise, addMinutes(new Date(), 5), true);
    forecastIcon = result.success ? 'fa-check' : 'fa-xmark';
    if (!result.success) notifyApiError(result, { code: ErrorCode.SCHEDULE_FORECAST_FAILED, message: 'Could not assign this training to your forecast. Please try again in a moment.' });
  }

  async function addToCalendar(dateTime: Date) {
    scheduleIcon = 'fa-spinner-third animate-spin';
    schedulePopoverOpen = false;
    const result = await scheduleTraining(exercise, dateTime, false);
    scheduleIcon = result.success ? 'fa-check' : 'fa-xmark';
    if (!result.success) notifyApiError(result, { code: ErrorCode.SCHEDULE_CALENDAR_FAILED, message: 'Could not add this training to your calendar. Please try again in a moment.' });
  }

  const scheduleAtChosenTime = () => addToCalendar(new Date(`${inputDate}T${inputTime}`));
  // scheduleTraining re-reads the clock and rejects anything already past, so "now" needs enough
  // headroom to survive the round trip.
  const scheduleNow = () => addToCalendar(addMinutes(new Date(), 1));

  // Event dispatch for Edit / Magic Buckets
  let eventDispatchHandler;
  onMount(() => {
    [eventDispatchHandler] = CreateCustomEventDispatchHandler(rootDiv ?? document.body);
  });

  function flagActivity() {
    Activity.toggleFlag(exercise);
    calendarLoader?.updateActivity(exercise, (e) => ({ ...e, flag: !isFlagged }));
    todaysActivities?.update((prev) => {
      return prev.map((a) => {
        if (a.path === exercise.path) {
          return { ...a, flag: !isFlagged };
        }
        return a;
      });
    });
  }

  function pinActivity() {
    Activity.togglePin(exercise);
    calendarLoader?.updateActivity(exercise, (e) => ({ ...e, pinned: !isPinned }));
    todaysActivities?.update((prev) => {
      return prev.map((a) => {
        if (a.path === exercise.path) {
          return { ...a, pinned: !isPinned };
        }
        return a;
      });
    });
  }

  function like() {
    if (exercise?.exerciseType === 'Workout') {
      likeWorkout(exercise.path);
      calendarLoader?.updateActivity(exercise, (e) => ({ ...e, liked: !exercise.liked, disliked: false }));
      todaysActivities?.update((prev) => {
        return prev.map((a) => {
          if (a.path === exercise.path) {
            return { ...a, liked: !a.liked, disliked: false };
          }
          return a;
        });
      });
    }
  }
  function dislike() {
    if (exercise?.exerciseType === 'Workout') {
      dislikeWorkout(exercise.path);
      calendarLoader?.updateActivity(exercise, (e) => ({ ...e, disliked: !exercise.disliked, liked: false }));
      todaysActivities?.update((prev) => {
        return prev.map((a) => {
          if (a.path === exercise.path) {
            return { ...a, disliked: !a.disliked, liked: false };
          }
          return a;
        });
      });
    }
  }
  function favourite() {
    if (exercise?.exerciseType === 'Activity') {
      starActivity(exercise.path, exercise.favorite);
      calendarLoader?.updateActivity(exercise, (e) => ({ ...e, favorite: !isStarred }));
      todaysActivities?.update((prev) => {
        return prev.map((a) => {
          if (a.path === exercise.path) {
            return { ...a, favorite: !isStarred };
          }
          return a;
        });
      });
    }
  }

  // Hide / Unhide
  function hideActivityWrapper() {
    hideActivity(exercise.path, isHidden);
    calendarLoader?.updateActivity(exercise, (e) => ({ ...e, enabled: isHidden }));
    todaysActivities.update((prev) => {
      return prev.map((a) => {
        if (a.path === exercise.path) {
          return { ...a, enabled: isHidden, matched_activity: isHidden ? a.matched_activity : null };
        }
        if (Activity.isMatchedAtoB(exercise, a)) {
          return { ...a, num_matches: Math.max(0, Math.max(0, a.num_matches) + (isHidden ? 1 : -1)) };
        }
        return a;
      });
    });
  }

  // Delete / Clear
  function deleteActivity() {
    Activity.clearFromCalendar(exercise);
    calendarLoader?.removeActivity(exercise);
    if (!isForecast) {
      todaysActivities.update((prev) => {
        return prev.filter((a) => a.path !== exercise.path).map((a) => {
          if (Activity.isMatchedAtoB(exercise, a)) {
            return { ...a, num_matches: Math.max(0, a.num_matches - 1) };
          }
          return a;
        });
      });
    }
  }

  // Send to device
  $: sendToDeviceIcons = Object.fromEntries($connectedWorkoutPlayers.players.map(wp => [wp.type, sendToDeviceIcons?.[wp.type] || 'fa-paper-plane']));

  async function sendTo(workoutPlayer: WorkoutPlayerType) {
    if (!workoutPath) return;
    sendToDeviceIcons[workoutPlayer] = 'fa-spinner-third animate-spin';
    sendToDeviceIcons = sendToDeviceIcons;
    const response = await connectedWorkoutPlayers.sendWorkoutToWorkoutPlayer(workoutPlayer, {...exercise, path: workoutPath});
    // The error toast is raised by connectedWorkoutPlayers.sendWorkoutToWorkoutPlayer.
    sendToDeviceIcons[workoutPlayer] = response.cancelled ? 'fa-paper-plane' : response.success ? 'fa-check' : 'fa-xmark';
    sendToDeviceIcons = sendToDeviceIcons;
  }

  // Downloads
  let downloads: ('zwo' | 'tcx' | 'erg')[];
  $: downloads = (exercise && workoutPath) ? ['zwo', 'tcx', 'erg'] : [];

  $: downloadWorkoutIcons = downloads?.map(() => 'fa-download');

  async function downloadWorkout(path: string, name: string, ext: 'zwo' | 'tcx' | 'erg') {
    downloadWorkoutIcons[downloads.indexOf(ext)] = 'fa-spinner-third animate-spin';
    const slopeIntervals = await askSlopeIntervals(ext, { ...exercise, path });
    if (slopeIntervals === null) {
      downloadWorkoutIcons[downloads.indexOf(ext)] = 'fa-download';
      return;
    }
    const result = await downloadWorkoutFile(path, name, ext, slopeIntervals);
    downloadWorkoutIcons[downloads.indexOf(ext)] = result.success ? 'fa-check' : 'fa-xmark';
    if (!result.success) notifyApiError(result, { code: ErrorCode.WORKOUT_DOWNLOAD_FAILED, message: `Could not download the ${ext.toUpperCase()} file for this workout. Please try again in a moment.` });
  }

  let downloadActivityIcon: string = 'fa-download';
  $: exercise, downloadActivityIcon = 'fa-download';

  async function downloadActivity(path: string, name: string) {
    downloadActivityIcon = 'fa-spinner-third animate-spin';
    const result = await downloadActivityFitFile(path, name);
    downloadActivityIcon = result.success ? 'fa-check' : 'fa-xmark';
    if (!result.success) notifyApiError(result, { code: ErrorCode.ACTIVITY_DOWNLOAD_FAILED, message: "Could not download this activity's file. Please try again in a moment." });
  }

  onMount(() => {
    jq('[data-toggle=tooltip]').tooltip({container: '#shadcn-ui-dialog-target'});
  });

</script>

<!-- Training -->
{#if isActivity && ((canEdit || !isGoalEvent) || (isPlaceholder && !noActivity && !isGoalEvent) || (isForecastEvent && !isPlaceholder))}
  <section class="space-y-3">
    <h3 class="text-sm font-medium tracking-wide text-muted-foreground">Training</h3>
    <div class="flex flex-col items-start gap-2">
      {#if canEdit}
        <Button size="sm" variant="outline" on:click={() => {
          eventDispatchHandler?.("editEvent", {date: new Date(exercise.start_date), exercise})();
          open = false;
        }}>
          <i class="fa-regular fa-pen-to-square text-xs mr-1"></i>{isForecastEvent ? 'Choose Training' : 'Edit'}
        </Button>
      {:else if !isGoalEvent}
        <!-- Don't show greyed out button for goal events -->
        <div data-toggle='tooltip' title='Cannot edit activities recorded with power data'>
          <Button size="sm" variant="outline" disabled>
            <i class="fa-regular fa-pen-to-square text-xs mr-1"></i>{isForecastEvent ? 'Choose Training' : 'Edit'}
          </Button>
        </div>
      {/if}
      {#if isPlaceholder && !noActivity && !isGoalEvent}
        <Button size="sm" variant="outline" on:click={() => {
          eventDispatchHandler?.("editEvent", {date: new Date(exercise.start_date), exercise, openTo: "magic"})();
          open = false;
        }}>
          <i class="fa-regular fa-sparkles text-xs mr-1"></i>Generate Workout
        </Button>
      {/if}
      {#if isForecastEvent && !isPlaceholder}
        <Button variant="outline" size="sm" on:click={pinActivity}>
          <i class={`${isPinned ? 'fa-solid' : 'fa-regular'} fa-thumbtack mr-1`}></i>{isPinned ? 'Unpin' : 'Pin'}
        </Button>
      {/if}
    </div>
  </section>
  <Separator class="bg-muted my-1 h-px w-full" />
{/if}

<!-- Schedule -->
{#if !hideScheduleButtons}
  <section class="space-y-3">
    <h3 class="text-sm font-medium tracking-wide text-muted-foreground">Schedule</h3>
    <div class="flex flex-col items-start gap-2">
      {#if $trainingPlan?.isForecastPlan}
        <div class='flex flex-row gap-2 items-center'>
          <Button size="sm" on:click={scheduleToForecast}>
            <i class={`fa-regular ${forecastIcon} text-xs mr-1`}></i>Assign to Forecast
          </Button>
          <HelpTooltip>
            <p class='text-sm'>
              Select a workout or activity that you plan to do as part of your training. If you plan on using Magic Buckets, no workout assignment is needed.
            </p>
            <p class='text-sm mt-2'>
              To plan a non-training ride such as a commute or gym workout, use the Schedule option.
            </p>
          </HelpTooltip>
        </div>
      {/if}
      <Popover.Root bind:open={schedulePopoverOpen} portal="#shadcn-ui-dialog-target">
        <Popover.Trigger asChild let:builder>
          <Button size="sm" variant={$trainingPlan?.isForecastPlan ? 'outline' : 'default'} builders={[builder]} on:click={(e) => e.stopPropagation()}>
            <i class={`fa-regular ${scheduleIcon} text-xs mr-1`}></i>{$trainingPlan?.isForecastPlan ? 'Schedule as Off-Plan Training' : `Do the ${trainingNoun}`}
          </Button>
        </Popover.Trigger>
        <Popover.Content side="top" class="flex flex-col gap-4">
          <div class="flex flex-row justify-between gap-4">
            <div class="space-y-1">
              <Label for="schedule-date">Date</Label>
              <Input type="date" id="schedule-date" bind:value={inputDate} />
            </div>
            <div class="space-y-1">
              <Label for="schedule-time">Time</Label>
              <Input type="time" id="schedule-time" bind:value={inputTime} />
            </div>
          </div>
          <div class="flex justify-end gap-2">
            <Button size="sm" variant="outline" on:click={scheduleNow}>Schedule Now</Button>
            <Button size="sm" on:click={scheduleAtChosenTime}>Add to Calendar</Button>
          </div>
        </Popover.Content>
      </Popover.Root>
    </div>
  </section>
  <Separator class="bg-muted my-1 h-px w-full" />
{/if}

<!-- Swap / Copy / Move -->
{#if isActivity && calendarLoader}
  <section class="space-y-3">
    <h3 class="text-sm font-medium tracking-wide text-muted-foreground">Calendar</h3>
    <div class="flex flex-col items-start gap-2">
      {#if isForecastEvent && !isGoalEvent}
        <Popover.Root bind:open={swapActivityPopoverOpen} portal="#shadcn-ui-dialog-target">
          <Popover.Trigger asChild let:builder>
            <Button builders={[builder]} variant='outline' size='sm' on:click={(e) => e.stopPropagation()} >
              <i class='fa-regular fa-swap text-xs mr-1' /> Swap
            </Button>
          </Popover.Trigger>
          <Popover.Content side='top' class="flex flex-col gap-4">
            <div class="flex flex-row justify-between">
              <div class="space-y-1">
                <Label for="selected-date">Date</Label>
                <Input type="date" id="selected-date" bind:value={inputDate} min={$today.toISOString().split('T')[0]}/>
              </div>
              <div class="space-y-1">
                <Label for="selected-time">Time</Label>
                <Input type="time" id="selected-time" bind:value={inputTime} />
              </div>
            </div>
            <div class="flex justify-end gap-2">
              <Button size="sm" variant="outline" on:click={() => {swapActivityPopoverOpen = false}}>
                Cancel
              </Button>
              <Button size="sm" variant="default" on:click={() => {
                const date = new Date(`${inputDate}T${inputTime}`);
                const targetActivity = calendarLoader.getActivitiesForDate(date).find((la) => Activity.isForecastEvent(la.activity))?.activity;
                if (targetActivity) {
                  swapCalendarEvents(exercise, targetActivity, () => calendarLoader.swapActivities(exercise, targetActivity), Xert);
                } else {
                  Xert?.warning('<strong>Warning:</strong> No forecast activity found for the selected date');
                }
                swapActivityPopoverOpen = false;
                open = false;
              }}>
                Swap
              </Button>
            </div>
          </Popover.Content>
        </Popover.Root>
      {/if}

      {#if canCopy}
        <Popover.Root bind:open={copyActivityPopoverOpen} portal="#shadcn-ui-dialog-target">
          <Popover.Trigger asChild let:builder>
            <Button size="sm" variant="outline" builders={[builder]} on:click={(e) => e.stopPropagation()}>
              <i class="fa-regular fa-copy text-xs mr-1"></i>Copy
            </Button>
          </Popover.Trigger>
          <Popover.Content side="top" class="flex flex-col gap-4">
            <div class="flex flex-row justify-between gap-4">
              <div class="space-y-1">
                <Label for="copy-date">Date</Label>
                <Input type="date" id="copy-date" bind:value={activityInputDate} />
              </div>
              <div class="space-y-1">
                <Label for="copy-time">Time</Label>
                <Input type="time" id="copy-time" bind:value={activityInputTime} />
              </div>
            </div>
            <div class="flex justify-end gap-2">
              <Button size="sm" variant="outline" on:click={() => { copyActivityPopoverOpen = false }}>Cancel</Button>
              <Button size="sm" on:click={() => {
                const date = new Date(`${activityInputDate}T${activityInputTime}`);
                copyCalendarEvent(exercise, date, () => calendarLoader.copyActivity(exercise, date), Xert);
                copyActivityPopoverOpen = false;
                open = false;
              }}>Copy</Button>
            </div>
          </Popover.Content>
        </Popover.Root>
      {:else}
        <div data-toggle='tooltip' title={isForecastEvent ? 'Cannot copy forecast activities' : 'Cannot copy this activity'}>
          <Button size="sm" variant="outline" disabled>
            <i class="fa-regular fa-copy text-xs mr-1"></i>Copy
          </Button>
        </div>
      {/if}

      {#if canMove}
        <Popover.Root bind:open={moveActivityPopoverOpen} portal="#shadcn-ui-dialog-target">
          <Popover.Trigger asChild let:builder>
            <Button size="sm" variant="outline" builders={[builder]} on:click={(e) => e.stopPropagation()}>
              <i class="fa-regular fa-calendar-day text-xs mr-1"></i>Move
            </Button>
          </Popover.Trigger>
          <Popover.Content side="top" class="flex flex-col gap-4">
            <div class="flex flex-row justify-between gap-4">
              <div class="space-y-1">
                <Label for="move-date">Date</Label>
                <Input type="date" id="move-date" bind:value={activityInputDate} min={isPlanned ? $today.toISOString().split('T')[0] : undefined} />
              </div>
              <div class="space-y-1">
                <Label for="move-time">Time</Label>
                <Input type="time" id="move-time" bind:value={activityInputTime} />
              </div>
            </div>
            <div class="flex justify-end gap-2">
              <Button size="sm" variant="outline" on:click={() => { moveActivityPopoverOpen = false }}>Cancel</Button>
              <Button size="sm" on:click={() => {
                const date = new Date(`${activityInputDate}T${activityInputTime}`);
                moveCalendarEvent(exercise, date, () => calendarLoader.moveActivity(exercise, date), Xert);
                moveActivityPopoverOpen = false;
                open = false;
              }}>Move</Button>
            </div>
          </Popover.Content>
        </Popover.Root>
      {:else}
        <div data-toggle='tooltip' title={isForecastEvent ? 'Cannot move forecast activities' : (isRecorded ? 'Cannot move recorded activities' : 'Cannot move this activity')}>
          <Button size="sm" variant="outline" disabled>
            <i class="fa-regular fa-calendar-day text-xs mr-1"></i>Move
          </Button>
        </div>
      {/if}
    </div>
  </section>
  <Separator class="bg-muted my-1 h-px w-full" />
{/if}

<!-- Share, send to Strava, open on the provider that recorded it -->
{#if isActivity && Activity.isRecorded(exercise) && hideScheduleButtons}
  <section class="space-y-3">
    <h3 class="text-sm font-medium tracking-wide text-muted-foreground">{garminLink ? 'Share, Strava & Garmin' : 'Share & Strava'}</h3>
    <div class="flex flex-wrap gap-2">
      <SendToStravaButton activity={exercise} {todaysActivities} {calendarLoader} disabled={loading} />
      <ShareActivityButton activity={exercise} disabled={loading} />
      {#if garminLink}
        <!-- Font Awesome has no Garmin brand glyph, so use the provider logo the rest of the app does. -->
        <Button size="sm" variant="outline" class="text-foreground no-underline" href={garminLink} target="garmin">
          <img src="/assets/global/img/providers/garmin.svg" alt="" class="h-3 w-3 mr-1" aria-hidden="true" />Open in Garmin
        </Button>
      {/if}
    </div>
  </section>
  <Separator class="bg-muted my-1 h-px w-full" />
{/if}

<!-- Send Workout to App / Device -->
{#if workoutPath && $connectedWorkoutPlayers?.players?.length > 0}
  <section class="space-y-3">
    <h3 class="text-sm font-medium tracking-wide text-muted-foreground">Send Workout to App / Device</h3>
    <div class="flex flex-wrap gap-2">
      {#each $connectedWorkoutPlayers.players as wp}
        {#if wp.type === 'garmin_connect' && exercise.total_intervals > 50}
          <div data-toggle='tooltip' title='Cannot send workouts with more than 50 intervals to Garmin Connect due to Garmin API limitations'>
            <Button disabled size="sm" variant="secondary">
              <i class={`fa-solid ${sendToDeviceIcons[wp.type]} text-xs mr-1`}></i>{workoutPlayerTypeToName[wp.type]}
            </Button>
          </div>
        {:else}
          <Button size="sm" variant="secondary" disabled={loading} on:click={() => sendTo(wp.type)}>
            <i class={`fa-solid ${sendToDeviceIcons[wp.type]} text-xs mr-1`}></i>{workoutPlayerTypeToName[wp.type]}
          </Button>
        {/if}
      {/each}
    </div>
  </section>
  <Separator class="bg-muted my-1 h-px w-full" />
{/if}

<!-- Downloads -->
{#await Activity.canDownload(exercise) then canDownload}
  {#if canDownload}
    <section class="space-y-3">
      <h3 class="text-sm font-medium tracking-wide text-muted-foreground">Download Fit File</h3>
      <div class="flex flex-wrap gap-2">
        <Button size='sm' variant='secondary' disabled={loading} on:click={() => {downloadActivity(exercise.path, exercise.name)}}>
          <i class={`fa-regular ${downloadActivityIcon} text-xs mr-1`} aria-hidden="true"></i>FIT
        </Button>
      </div>
    </section>
    <Separator class="bg-muted my-1 h-px w-full" />
  {/if}
{/await}
{#if workoutPath}
  <section class="space-y-3">
    <h3 class="text-sm font-medium tracking-wide text-muted-foreground">Download Workout</h3>
    <div class="flex flex-wrap gap-2">
      {#each downloads as d}
        <Button size='sm' variant='secondary' disabled={loading} on:click={() => {downloadWorkout(workoutPath, exercise.name, d)}}>
          <i class={`fa-regular ${downloadWorkoutIcons[downloads.indexOf(d)]} text-xs mr-1`} aria-hidden="true"></i>{d.toUpperCase()}
        </Button>
      {/each}
    </div>
  </section>
  <Separator class="bg-muted my-1 h-px w-full" />
{/if}

<!-- Your Feedback -->
{#if isRecorded || (isForecastEvent && !isPlaceholder) || exercise?.exerciseType === 'Activity' || exercise?.exerciseType === 'Workout'}
  <section class="space-y-3">
    <h3 class="text-sm font-medium tracking-wide text-muted-foreground">Your Feedback</h3>
    <div class="flex flex-wrap gap-2">
      {#if isActivity && isRecorded && hideScheduleButtons}
        <Button variant="outline" size="sm" on:click={flagActivity}>
          <i class={`${isFlagged ? 'fa-solid' : 'fa-regular'} fa-flag mr-1`}></i>{isFlagged ? 'Unflag' : 'Flag'}
        </Button>
      {/if}
      {#if exercise?.exerciseType === 'Activity'}
        <Button variant="outline" size="sm" on:click={favourite}>
          {#if exercise?.favorite}
            <i class="fa-solid fa-star mr-1"></i>Unfavourite
          {:else}
            <i class="fa-regular fa-star mr-1"></i>Favourite
          {/if}
        </Button>
      {:else if exercise?.exerciseType === 'Workout'}
        <Button variant="outline" size="sm" on:click={like}>
          <i class={`${exercise?.liked ? 'fa-solid' : 'fa-regular'} fa-thumbs-up mr-1`}></i>Like
        </Button>
        <Button variant="outline" size="sm" on:click={dislike}>
          <i class={`${exercise?.disliked ? 'fa-solid' : 'fa-regular'} fa-thumbs-down mr-1`}></i>Dislike
        </Button>
      {/if}
    </div>
  </section>
  <Separator class="bg-muted my-1 h-px w-full" />
{/if}

<!-- Manage -->
{#if isActivity && ((isRecorded && !isPlanned) || !isPlaceholder) && hideScheduleButtons}
  <section class="space-y-3">
    <h3 class="text-sm font-medium tracking-wide text-muted-foreground">Manage</h3>
    <div class="flex flex-wrap gap-2">
      {#if isRecorded && !isPlanned && !isDuplicate}
        <Button variant="outline" size="sm" on:click={hideActivityWrapper}>
          <i class={`${isHidden ? 'fa-solid' : 'fa-regular'} fa-eye-slash mr-1`}></i>{isHidden ? 'Unhide' : 'Hide'}
        </Button>
      {/if}
      {#if !isPlaceholder}
        <ConfirmationPopover title="{isForecastEvent ? 'Clear' : 'Delete'} this activity?" onConfirm={deleteActivity} let:builder>
          <Button variant="outline" size="sm" builders={[builder]} on:click={(e) => e.stopPropagation()}>
            <i class="fa-regular {isForecastEvent ? 'fa-xmark-large' : 'fa-trash'} mr-1"></i>{isForecastEvent ? 'Clear' : 'Delete'}
          </Button>
        </ConfirmationPopover>
      {/if}
    </div>
  </section>
{/if}
